<template>
  <TransitionRoot appear :show="props.modelValue" as="template">
    <Dialog as="div" @close="closeModal" class="relative z-[9999999]">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black bg-opacity-25" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              class="w-full max-w-md transform rounded-2xl bg-white p-6 text-left align-middle transition-all"
            >
              <DialogTitle
                as="h3"
                class="text-lg font-medium leading-6 text-gray-900 mb-4"
              >
                Switch Store
              </DialogTitle>

              <div class="mt-4">                
                <div v-if="isLoading" class="text-center py-8">
                  <LoadingState />
                </div>

                <div v-else>
                  <div class="relative">
                    <input
                      v-model="search"
                      @input="filterStores"
                      @focus="showDropdown = true"
                      type="text"
                      placeholder="Search for a store..."
                      class="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 pr-10 placeholder-gray-400 shadow-sm focus:border-untitled-gray-500 focus:outline-none focus:ring-untitled-gray-500"
                    />

                    <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                      <ChevronDownIcon class="h-5 w-5" aria-hidden="true" />
                    </div>

                    <div
                      v-if="showDropdown"
                      class="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
                    >

                      <div v-if="filteredStores.length > 0">
                        <div
                          v-for="store in filteredStores"
                          :key="store.id"
                          @click="selectStore(store)"
                          class="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-untitled-gray-50"
                          :class="{ 'bg-untitled-gray-100': selectedStore?.id === store.id }"
                        >
                          <div class="flex items-center">
                            <span class="block truncate font-normal">
                              {{ store.name }}
                            </span>
                            <span
                              v-if="selectedStore?.id === store.id"
                              class="absolute inset-y-0 right-0 flex items-center pr-4 text-untitled-gray-600"
                            >
                              <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path
                                  fill-rule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clip-rule="evenodd"
                                />
                              </svg>
                            </span>
                          </div>
                        </div>
                      </div>

                      <div
                        v-else-if="filteredStores.length === 0 && search"
                        class="py-2 px-3 text-sm text-gray-500"
                      >
                        No stores found
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="mt-6 flex justify-between items-center">
                <div>
                   <button
                    v-if="selectedStore"
                    type="button"
                    @click="handleSwitchBack"
                    :disabled="isSwitching"
                    class="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                  >
                    Switch Back
                  </button>
                </div>
                <div class="flex gap-3">
                  <button
                    type="button"
                    @click="closeModal"
                    class="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-untitled-gray-500 focus-visible:ring-offset-2"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    @click="switchStore"
                    :disabled="isSwitching"
                    class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-untitled-gray-800 bg-untitled-gray-200 hover:bg-untitled-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-untitled-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {{ isSwitching ? 'Loading...' : 'Save' }}
                  </button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { TransitionRoot, TransitionChild, Dialog, DialogPanel, DialogTitle } from "@headlessui/vue";
import { ref, inject, defineProps, defineEmits, onMounted, watch } from "vue";
import Bugsnag from "@bugsnag/js";
import LoadingState from "@/components/partials/LoadingState.vue";
import eventBus from "@/plugins/eventBus";
import { ChevronDownIcon } from "@heroicons/vue/solid";
import { useAuthStore } from "@/stores";
import { storeToRefs } from "pinia";
const emit = defineEmits(["update:modelValue"]);
const props = defineProps({
  modelValue: {
    default: false,
    type: Boolean,
  },
});
const axios = inject("axios");
const authStore = useAuthStore();
const { user } = storeToRefs(authStore);
const search = ref("");
const showDropdown = ref(false);
const isLoading = ref(false);
const isSwitching = ref(false);
const stores = ref([]);
const filteredStores = ref([]);
const selectedStore = ref(null);
const closeModal = () => {
  emit("update:modelValue", false);
  showDropdown.value = false;
};
const fetchStores = async () => {
  isLoading.value = true;
  try {
    const params = {
      per_page: 1000,
    };
    if (user.value && user.value.dealer_id) {
        params.company_id = user.value.dealer_id;
    }
    const { data } = await axios.get("/api/v3/specific_dealers", { params });
    
    let fetchedStores = Array.isArray(data) ? data : (data.data || []);
    if (user.value && user.value.dealer_id) {
        fetchedStores = fetchedStores.filter(store => store.dealer_id === user.value.dealer_id);
    }
    stores.value = fetchedStores;
    filteredStores.value = stores.value;
    
    const storedStore = localStorage.getItem("selected_store");
    if (storedStore) {
      const currentStoreData = JSON.parse(storedStore);
      const matchedStore = stores.value.find(s => s.id === currentStoreData.id);
      
      if (matchedStore) {
        selectedStore.value = matchedStore;
        search.value = matchedStore.name;
      }
    } else {
      selectedStore.value = null;
      search.value = "";
    }
  } catch (error) {
    Bugsnag.notify(error);
    eventBus.$emit(
      "CREATE_NOTIFICATION",
      "Failed to load stores. Please try again.",
      "error",
      5000
    );
  } finally {
    isLoading.value = false;
  }
};
const filterStores = () => {
  const searchTerm = search.value.toLowerCase().trim();
  
  if (!searchTerm) {
    filteredStores.value = stores.value;
  } else {
    filteredStores.value = stores.value.filter((store) =>
      store.name.toLowerCase().includes(searchTerm)
    );
  }
  showDropdown.value = true;
};
const unselectStore = () => {
  selectedStore.value = null;
  search.value = "";
  showDropdown.value = false;
};
const selectStore = (store) => {
  if (selectedStore.value && selectedStore.value.id === store.id) {
    unselectStore();
  } else {
    selectedStore.value = store;
    search.value = store.name;
    showDropdown.value = false;
  }
};
const handleSwitchBack = () => {
  selectedStore.value = null;
  switchStore();
};
const switchStore = async () => {
  isSwitching.value = true;
  try {
    const storedStore = localStorage.getItem("selected_store");
    let currentStoreId = storedStore ? JSON.parse(storedStore).id : null;
    let newStoreId = selectedStore.value ? selectedStore.value.id : null;
    
    if (newStoreId === null && currentStoreId === null) {
        closeModal();
        isSwitching.value = false;
        return;
    }
    if (newStoreId === currentStoreId) {
       closeModal();
       isSwitching.value = false;
       return; 
    }
    if (selectedStore.value === null) {
      localStorage.removeItem("selected_store");
      eventBus.$emit("STORE_SWITCHED", null);
      
      eventBus.$emit(
        "CREATE_NOTIFICATION",
        "Store filter cleared. Viewing all stores.",
        "success",
        3000
      );
    } else {
      localStorage.setItem("selected_store", JSON.stringify(selectedStore.value));
      eventBus.$emit("STORE_SWITCHED", selectedStore.value);
      
      eventBus.$emit(
        "CREATE_NOTIFICATION",
        `Successfully switched to ${selectedStore.value.name}`,
        "success",
        3000
      );
    }
    
    closeModal();
    window.location.reload();
  } catch (error) {
    Bugsnag.notify(error);
    eventBus.$emit(
      "CREATE_NOTIFICATION",
      "Failed to switch store. Please try again.",
      "error",
      5000
    );
  } finally {
    isSwitching.value = false;
  }
};
const handleClickOutside = (event) => {
  const dropdownElement = event.target.closest('.relative');
  
  const inputElement = dropdownElement ? dropdownElement.querySelector('input') : null;
  if (dropdownElement && event.target !== inputElement) {
    showDropdown.value = false;
  }
};
onMounted(() => {
  fetchStores();
  document.addEventListener("click", handleClickOutside);
});
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue) {
      fetchStores();
    }
  }
);
</script>